/* Shared realtime client: a best-effort push layer over the existing polling, load-bearing for nothing. */

const registry = new Map(); // url -> connection

/* Two doors to the same socket. /ws/ is the documented path; /api/ws/ is the
   same consumer under the prefix every proxy already forwards for the REST
   calls. A connection that has never opened alternates between them on each
   failed attempt and then keeps whichever worked - so a front proxy with no
   rule for /ws/ costs a second or two of retry, not the whole realtime layer. */
const WS_PATHS = ['/ws/notifications/', '/api/ws/notifications/'];

/* Is ANY socket to the server open right now? One answer for every board:
   they share this registry, so if one is up they all are. */
export function isRealtimeOpen() {
  for (const c of registry.values()) if (c.isOpen()) return true;
  return false;
}

/* How often a board re-reads over REST while it cannot hear the server. */
const FALLBACK_FAST_MS = 5000;

/* Every running fallback poll, so a socket that DROPS can wake them at once
   rather than each waiting out whatever long interval it last scheduled. */
const pollWakers = new Set();

/* The safety-net poll under every live board - made to MATTER when the
   socket is down. A fixed 15-20s was chosen for a board that normally hears
   every change pushed and polls only to be sure; with the socket broken (a
   proxy dropping the upgrade, Daphne down) that poll is the ONLY way news
   arrives, and a kitchen learned of a paid order up to twenty seconds late.
   This reschedules itself each tick and reads the socket's state as it
   does: 5s while deaf, the old cadence the moment the socket is open. */
export function fallbackPoll(fn, everyMs) {
  let id = null;
  let stopped = false;
  const tick = () => {
    if (stopped) return;
    try { fn(); } catch { /* the caller's own load handles its errors */ }
    id = setTimeout(tick, isRealtimeOpen() ? everyMs : FALLBACK_FAST_MS);
  };
  id = setTimeout(tick, isRealtimeOpen() ? everyMs : FALLBACK_FAST_MS);
  // Called when a socket closes: throw away the long wait and poll soon.
  const wake = () => {
    if (stopped) return;
    if (id) clearTimeout(id);
    id = setTimeout(tick, FALLBACK_FAST_MS);
  };
  pollWakers.add(wake);
  return () => { stopped = true; pollWakers.delete(wake); if (id) clearTimeout(id); };
}

/* How many never-open attempts count as a blip before the push layer is written off for this page load. */
const GIVE_UP_AFTER = 4;

function wsUrlFor(token, params = {}) {
  const api = process.env.REACT_APP_API_URL || '';
  let origin;
  if (/^https?:\/\//i.test(api)) origin = api.replace(/\/api\/?$/, '');
  else origin = window.location.origin; // relative '/api' → same origin (prod)
  const wsOrigin = origin.replace(/^http/i, 'ws'); // http→ws, https→wss
  const q = new URLSearchParams({ token: token || '' });
  // Context for the realtime dashboard (which app / location is connected).
  if (params.app) q.set('app', params.app);
  if (params.location != null) q.set('location', String(params.location));
  return `${wsOrigin}/ws/notifications/?${q.toString()}`;
}

function getConnection(url) {
  let conn = registry.get(url);
  if (conn) return conn;

  conn = {
    url,
    attempt: 0,
    goodPath: null,
    lastPath: null,
    ws: null,
    subs: new Set(),      // { kinds:Set|null, handler }
    backoff: 1000,
    heartbeat: null,
    closedByUs: false,
    // Has this URL ever produced a working socket? A proven link gets the full ladder, an unproven one a few tries.
    everOpen: false,
    failures: 0,
  };

  conn.isOpen = () => !!conn.ws && conn.ws.readyState === WebSocket.OPEN;

  const emit = (msg) => {
    conn.subs.forEach((s) => {
      if (!s.kinds || s.kinds.has(msg.kind)) {
        try { s.handler(msg); } catch { /* ignore subscriber errors */ }
      }
    });
  };

  const connect = () => {
    let ws;
    const path = conn.goodPath || WS_PATHS[conn.attempt % WS_PATHS.length];
    conn.lastPath = path;
    const target = url.replace('/ws/notifications/', path);
    try { ws = new WebSocket(target); } catch { conn.attempt += 1; schedule(); return; }
    conn.ws = ws;
    ws.onopen = () => {
      conn.goodPath = conn.lastPath;
      conn.backoff = 1000;
      conn.everOpen = true;
      conn.failures = 0;
      conn.heartbeat = setInterval(() => {
        try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* noop */ }
      }, 25000);
    };
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg && msg.kind) emit(msg);
    };
    ws.onclose = (ev) => {
      clearInterval(conn.heartbeat);
      conn.ws = null;
      /* Synthetic, since the server can't tell us it's gone; lets a link-state screen fall back to "connecting". */
      emit({ kind: 'disconnected' });
      // The boards' safety-net polls: from the old cadence to 5s, now.
      pollWakers.forEach((wake) => wake());
      if (!conn.goodPath) conn.attempt += 1;
      /* 4401 means the token was refused, and retrying cannot fix a rejected credential - stop and let polling carry it. */
      if (ev && ev.code === 4401) {
        conn.refused = true;
        emit({ kind: 'refused' });
        return;
      }
      /* Closed without ever opening means the handshake failed, so retry a few times and then stay quiet. */
      if (!conn.everOpen) {
        conn.failures += 1;
        if (conn.failures >= GIVE_UP_AFTER) {
          conn.unavailable = true;
          emit({ kind: 'unavailable' });
          return;
        }
      }
      if (!conn.closedByUs && conn.subs.size > 0) schedule();
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  };

  const schedule = () => {
    const wait = Math.min(conn.backoff, 30000);
    conn.backoff = wait * 2;
    setTimeout(() => { if (!conn.closedByUs && conn.subs.size > 0) connect(); }, wait);
  };

  conn.connect = connect;
  registry.set(url, conn);
  connect();
  return conn;
}

/** Subscribe to realtime events by kind, authenticating with the staff JWT unless a token is passed. */
export function subscribeRealtime(kinds, handler, opts = {}) {
  const token =
    opts.token || localStorage.getItem('sessionToken') || localStorage.getItem('token');
  if (!token) return () => {};
  const url = wsUrlFor(token, { app: opts.app || 'web', location: opts.location });
  const conn = getConnection(url);
  const sub = { kinds: kinds ? new Set(kinds) : null, handler };
  conn.subs.add(sub);
  /* Replay the server's "connected" greeting locally, so a subscriber joining an open socket doesn't think it's down. */
  if (conn.isOpen() && (!sub.kinds || sub.kinds.has('connected'))) {
    setTimeout(() => {
      if (conn.subs.has(sub) && conn.isOpen()) {
        try { handler({ kind: 'connected', shared: true }); } catch { /* ignore */ }
      }
    }, 0);
  }
  /* And the terminal states, for the same reason in reverse: a subscriber
     joining a connection that already gave up (or was refused) would
     otherwise hear nothing at all and read "connecting" for ever. */
  const ended = conn.unavailable ? 'unavailable' : conn.refused ? 'refused' : null;
  if (ended && (!sub.kinds || sub.kinds.has(ended))) {
    setTimeout(() => {
      if (conn.subs.has(sub)) {
        try { handler({ kind: ended, replayed: true }); } catch { /* ignore */ }
      }
    }, 0);
  }
  return () => {
    conn.subs.delete(sub);
    if (conn.subs.size === 0) {
      conn.closedByUs = true;
      try { conn.ws && conn.ws.close(); } catch { /* noop */ }
      clearInterval(conn.heartbeat);
      registry.delete(url);
    }
  };
}
